"use client";

import {isPresentationRecord, presentedFields} from "@repo/ui/gailgpt/tool-presentation";
import {ReviewFields, type ToolDetailsProps} from "./ToolPresentationDetails";
import FrozenValue from "./FrozenValue";
import {isUseCaseStep} from "./useCaseSnapshot";
import {useTranslation} from "react-i18next";
import {occurrenceKeys, valueKey} from "./snapshotKeys";

/** Keep the review's reading order independent of JSON property and metadata order. */
export default function UseCaseToolDetails({presentation, parameters, batch = false}: ToolDetailsProps & {batch?: boolean}) {
   const {t} = useTranslation("ask");
   const order = ["title", "description", "actions"];
   const property = (path: string[]) => {
      if (path.length === 1) return path[0];
      if (path.length === 2 && path[0] === "body") return path[1];
      return undefined;
   };
   const rank = (path: string[]) => {const index = order.indexOf(property(path) ?? ""); return index < 0 ? order.length : index;};
   const fields = presentedFields(parameters, presentation.fields).sort((a, b) => rank(a.path) - rank(b.path)).map(field => {
      if (property(field.path) !== "actions" || !Array.isArray(field.value)) return field;
      return {...field, value: field.value.map(action => isPresentationRecord(action)
         ? Object.fromEntries(Object.entries(action).filter(([key, value]) => key !== "textScriptId" || (value != null && value !== "")))
         : action)};
   });
   return <div className={batch ? "approval-batch-fields" : "px-5 pb-5 pt-4"}><ReviewFields fields={batch ? fields.filter(field => property(field.path) !== "title") : fields}
      renderField={batch ? field => {
         if (property(field.path) === "description") return <div className="grid grid-cols-[56px_minmax(0,1fr)] gap-x-2.5 text-approval-body leading-approval-body">
            <span className="text-xs font-medium text-600">{t("toolApproval.batch.goal")}</span>
            <div><FrozenValue value={field.value} /></div>
         </div>;
         if (property(field.path) !== "actions" || !Array.isArray(field.value) || !field.value.length || !field.value.every(isUseCaseStep)) return undefined;
         return <div className="space-y-1.5"><p className="text-xs font-medium text-600">{t("toolApproval.fields.actions")}</p>
            <ol className="space-y-2">{occurrenceKeys(field.value, valueKey).map(({item: action, key}, index) => {
               const extras = Object.fromEntries(Object.entries(action).filter(([key]) => key !== "description" && key !== "type"));
               return <li key={key} className="grid grid-cols-[18px_minmax(0,1fr)] gap-1 text-approval-body leading-approval-body">
                  <span className="text-500">{index + 1}.</span><div className="space-y-1">
                     {Object.hasOwn(action, "type") && action.type !== "default" && <div className="text-xs font-medium text-600"><FrozenValue value={action.type} field="type" /></div>}
                     <FrozenValue value={action.description} />
                     {!!Object.keys(extras).length && <FrozenValue value={extras} path={{source: "request", segments: [...field.path, String(index)]}} humanizeLabels />}
                  </div>
               </li>;
            })}</ol>
         </div>;
      } : undefined} /></div>;
}
